import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  fract, floor, sin, mix, smoothstep, clamp, uv, min
} from 'three/tsl';

// Procedural cloud layer for the splash→home transition frame. Rendered onto
// a fullscreen quad with an ortho camera, so the shader owns the full-frame
// pixel work and the CSS gradient/cloud divs it replaces are gone.
//
// Design:
//   - Sky gradient baked into the shader so the transition frame reads as one
//     continuous "look up through atmosphere" column (deep teal at the top,
//     cyan at the bottom, same stops as the old CSS gradient).
//   - Two cloud layers (far + near), each a domain-warped fbm with a height
//     band so cloud cover forms in a believable horizontal strip rather than
//     filling the whole sky. The near layer sits slightly lower and drifts
//     faster, giving depth without costing a third octave stack.
//   - Lighting: a "sun probe" fbm sample offset UP-LEFT is subtracted from
//     the local density — points that are denser than the probe are IN the
//     shadow of what's above, and points that are less dense get lit. This
//     is the canonical cheap-trick for lit cloud tops / shaded undersides
//     without needing a real raymarched volume.
//
// Shader uses uv() directly, so the consumer mounts a PlaneGeometry(2,2) with
// an ortho camera spanning [-1, 1] — the uv attribute gives [0,1]² across
// the quad automatically.

export interface CloudsOptions {
  /** width / height of the canvas this material is rendered onto. Tunes
   *  noise scaling so cloud frequency is consistent across aspect ratios. */
  aspect?: number;
}

export class CloudsMaterial extends NodeMaterial {
  readonly uTime   = uniform(0);
  readonly uAspect = uniform(1.0);
  // Sky gradient stops — top / horizon / bottom. Match the old CSS gradient
  // so the existing splash-frame → transition-frame seam stays clean.
  readonly uSkyTop = uniform(new THREE.Color('#01222D'));
  readonly uSkyMid = uniform(new THREE.Color('#2F7F9E'));
  readonly uSkyBot = uniform(new THREE.Color('#4DB1CF'));

  constructor(opts: CloudsOptions = {}) {
    super();
    this.uAspect.value = opts.aspect ?? 1;
    this.depthTest = false;
    this.depthWrite = false;
    this.lights = false;

    const hash2 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      return fract(sin(p.dot(vec2(127.1, 311.7))).mul(43758.5453));
    });

    // Value noise, 2D. Bilinear lerp with cubic weights. Identical to
    // vnoise2 in ./tsl/noise — inlined here so the splash doesn't have to
    // pull the shared helpers (which already live in materials used by the
    // in-game renderer).
    const vnoise = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const u = f.mul(f).mul(float(3).sub(f.mul(2)));
      const a = hash2(i);
      const b = hash2(i.add(vec2(1, 0)));
      const c = hash2(i.add(vec2(0, 1)));
      const d = hash2(i.add(vec2(1, 1)));
      return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
    });

    // 6-octave fbm. Loop unrolls into a flat node graph.
    const fbm6 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 6; i++) {
        sum.addAssign(vnoise(q).mul(amp));
        q.assign(q.mul(2.03).add(vec2(31.7, 17.3)));
        amp.assign(amp.mul(0.52));
      }
      return sum;
    });

    // Cheap 3-octave fbm for the light probe — only needs to be smooth, not
    // detailed.
    const fbm3 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 3; i++) {
        sum.addAssign(vnoise(q).mul(amp));
        q.assign(q.mul(2.17).add(vec2(13.1, 23.7)));
        amp.assign(amp.mul(0.55));
      }
      return sum;
    });

    this.colorNode = Fn(() => {
      // uv is (0,0) bottom-left to (1,1) top-right on a default PlaneGeometry.
      // Flip Y so sy=0 is the TOP of the sky and sy=1 is the horizon —
      // reads more naturally for "upper atmosphere / lower atmosphere"
      // thresholds below.
      const p = uv().toVar();
      const sy = float(1).sub(p.y).toVar();

      // -------- Sky gradient --------
      // Two-stop lerp: top→mid in the upper 60%, mid→bot in the lower 45%.
      // Overlap band (0.55–0.60) keeps the transition smooth.
      const skyA = mix(this.uSkyTop, this.uSkyMid, smoothstep(0.0, 0.6, sy));
      const sky = mix(skyA, this.uSkyBot, smoothstep(0.55, 1.0, sy));

      // -------- Noise coordinate space --------
      // Aspect-correct x so cloud puffs stay circular-ish regardless of
      // viewport ratio. Y stretched 2× vs x so clouds read wider than tall
      // (matches real cumulus silhouettes under a shallow viewing angle).
      const cx = p.x.mul(this.uAspect).mul(3.2).toVar();
      const cy = sy.mul(4.4).toVar();
      const c = vec2(cx, cy).toVar();
      const t = this.uTime;

      // -------- Far layer — small-scale, slow, hazy --------
      // Base coord drifts slowly so the far clouds read as "barely moving
      // relative to camera". Domain warp is a low-amplitude vec2 noise
      // field sampled at a different offset — IQ-style curl that gives
      // wispy, non-grid cloud outlines rather than blobs aligned to the
      // fbm frequency.
      const p0 = c.mul(0.55).add(vec2(t.mul(0.015), t.mul(-0.003))).toVar();
      const w0 = vec2(
        vnoise(p0.add(vec2(5.1, 3.2))).sub(0.5),
        vnoise(p0.add(vec2(11.3, 7.8))).sub(0.5)
      ).mul(1.4);
      const f0 = fbm6(p0.add(w0)).toVar();

      // Height band: far clouds form a high thin layer. Smoothstep-in from
      // sy=0.18 and smoothstep-out by sy=0.70 so the far deck hangs in the
      // upper half of the canvas.
      const band0 = smoothstep(0.12, 0.30, sy).mul(smoothstep(0.78, 0.55, sy));
      const density0 = smoothstep(0.50, 0.70, f0).mul(band0).toVar();

      // -------- Near layer — larger, faster, chunkier --------
      const p1 = c.mul(0.95).add(vec2(t.mul(0.045), t.mul(-0.010))).toVar();
      const w1 = vec2(
        vnoise(p1.mul(0.8).add(vec2(19.1, 3.2))).sub(0.5),
        vnoise(p1.mul(0.8).add(vec2(2.3, 27.8))).sub(0.5)
      ).mul(1.7);
      const f1 = fbm6(p1.add(w1)).toVar();
      const band1 = smoothstep(0.25, 0.48, sy).mul(smoothstep(0.98, 0.70, sy));
      const density1 = smoothstep(0.50, 0.72, f1).mul(band1).toVar();

      // -------- Lighting --------
      // Sun is up-left in our world. In noise coord: x decreasing & y
      // decreasing (sy=0 is top, and we offset in p-space where cy grew
      // positively downward). So "toward sun" = add a vec2(-,-).
      //
      // Light probe: sample the cloud field a short distance toward the sun.
      // If that sample is denser than the local density, the local pixel is
      // below/behind occluding cloud → shadow. If less dense, sunlight
      // reaches this pixel → lit.
      //
      // We clamp and bias to keep dim edges readable rather than pitch-blue.
      const sunOff = vec2(-0.35, -0.55);
      const probe0 = fbm3(p0.add(w0).add(sunOff));
      const lit0 = clamp(f0.sub(probe0.mul(0.85)).mul(1.8).add(0.35), 0, 1).toVar();

      const probe1 = fbm3(p1.add(w1).add(sunOff));
      const lit1 = clamp(f1.sub(probe1.mul(0.85)).mul(1.8).add(0.30), 0, 1).toVar();

      // Palette: lit tops near-white with a warm highlight, shaded undersides
      // blue-grey so they read as atmospheric scatter picking up the sky
      // rather than flat grey.
      const cloudLit    = vec3(1.00, 0.98, 0.94);
      const cloudMid    = vec3(0.78, 0.86, 0.93);
      const cloudShadow = vec3(0.38, 0.52, 0.64);

      const shade = (l: Node<'float'>): Node<'vec3'> =>
        mix(cloudShadow, mix(cloudMid, cloudLit, smoothstep(0.45, 0.9, l)), smoothstep(0.0, 0.55, l));

      const col0 = shade(lit0);
      const col1 = shade(lit1);

      // Soft tint the far layer toward horizon-sky so the two decks read
      // as different distances (near clouds more saturated, far clouds
      // slightly washed into the atmosphere).
      const farTint = mix(col0, sky, 0.18);

      // Alpha composite back-to-front. Slight knockdown on far-layer alpha
      // so the near layer visually dominates when they overlap.
      const withBack = mix(sky, farTint, density0.mul(0.82));
      const withFront = mix(withBack, col1, density1);

      // Gentle horizon glow — a thin warm band peaking around sy≈0.92,
      // tapering back to zero exactly at sy=1. Exact-zero endpoint is
      // load-bearing: the transition-frame's bottom pixel must equal
      // uSkyBot (#4DB1CF) so it lines up seamlessly with the home's
      // own top-colour. Any residual glow at the seam would produce a
      // visible colour step when the whoosh lands.
      const horizon = smoothstep(0.72, 0.92, sy).mul(float(1).sub(smoothstep(0.88, 1.00, sy)));
      const horizonTint = vec3(1.00, 0.85, 0.65);
      const horizonGlow = horizonTint.mul(horizon).mul(0.10).mul(float(1).sub(min(density0.add(density1), 1)));

      const final = withFront.add(horizonGlow);
      // No post-grade: keeping the sky colours linear here matters because
      // the transition-frame's bottom edge must read as exactly uSkyBot
      // (#4DB1CF), which is also the top of the home screen's own CSS
      // background gradient. Any contrast multiplier down here would break
      // that seam and produce a visible colour step as the whoosh lands.

      return vec4(final, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
  setAspect(a: number): void { this.uAspect.value = a; }
}
